import { createGlobalStyle, withTheme, css, type DefaultTheme } from 'styled-components';
import { IS_TABBING_CLASS } from './constants';

const createCSS = (theme: DefaultTheme): ReturnType<typeof css> => {
  let styles = '';

  for (let i = 1; i <= 64; i += 1) {
    styles += `
    .padding-left-${i} {
      padding-left: ${theme.grid.units(i)};
    }
    .padding-right-${i} {
      padding-right: ${theme.grid.units(i)};
    }
    .margin-left-${i} {
      margin-left: ${theme.grid.units(i)};
    }
    .margin-right-${i} {
      margin-right: ${theme.grid.units(i)};
    }
    `;
  }

  for (let i = 1; i <= 10; i += 1) {
    styles += `
    .padding-top-${i} {
      padding-top: ${theme.grid.units(i)};
    }

    .padding-${i} {
      padding-top: ${theme.grid.units(i)};
      padding-bottom: ${theme.grid.units(i)};
      padding-left: ${theme.grid.units(i)};
      padding-right: ${theme.grid.units(i)};
    }
    .padding-bottom-${i} {
      padding-bottom: ${theme.grid.units(i)};
    }
    .margin-top-${i} {
      margin-top: ${theme.grid.units(i)};
    }
    .margin-bottom-${i} {
      margin-bottom: ${theme.grid.units(i)};
    }
    .max-width-${i} {
      max-width: ${theme.grid.units(i)};
    }

    .min-width-${i} {
      min-width: ${theme.grid.units(i)};
    }
    .stacked-modal-${i} {
      position: relative;
      top: ${theme.grid.units(i)};
      left: ${theme.grid.units(i)};
    }
    `;
  }

  for (let i = 1; i <= 10; i += 1) {
    styles += `
    .width-percentage-${i} {
      width: round(percentage(${i}/100));
    }

    .min-height-percentage-${i} {
      min-height: round(percentage(${i}/100));
    }

    .min-width-percentage-${i} {
      min-width: round(percentage(${i}/100));
    }
    `;
  }

  for (let i = 1; i <= 32; i += 1) {
    styles += `
    .col-${i} {
      width: percentage(${i} / 32);
    }
    .height-${i} {
      height: ${theme.grid.units(i)};
    }

    .min-height-${i} {
      min-height: ${theme.grid.units(i)};
    }

    .max-height-${i} {
      max-height: ${theme.grid.units(i)};
    }
    `;
  }
  return css`
    ${styles}
  `;
};

const GlobalStyles = createGlobalStyle`
  ${({ theme }) => createCSS(theme)}

  html {
    min-height: 100vh;
    margin: 0;
    padding: 0;
    background-color: ${({ theme }) => theme.colors.black};
    font-size: ${({ theme }) => theme.fontSizes.p};
    font-family: ${({ theme }) => theme.fontFamilies.default};

    & * {
      box-sizing: border-box;
      font-family: ${({ theme }) => theme.fontFamilies.default};
    }
  }
  body {
    background-color: ${({ theme }) => theme.colors.white};
    min-height: 100vh;
    width: 100%;
    margin: 0;
    padding: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: antialiased;
    #App {
      min-height: 100%;
      width: 100%;
      margin: 0;
      padding: 0;
    }
  }
  p {
    font-size: ${({ theme }) => theme.fontSizes.p};
    margin: .7em 0;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    padding: 0;
    margin: 0;
    line-height: 100%;
  }
  h1 {
    font-size: ${({ theme }) => theme.fontSizes.h1};
  }
  h2 {
    font-size: ${({ theme }) => theme.fontSizes.h2};
  }
  h3 {
    font-size: ${({ theme }) => theme.fontSizes.h3};
  }
  h4 {
    font-size: ${({ theme }) => theme.fontSizes.h4};
  }
  h5 {
    font-size: ${({ theme }) => theme.fontSizes.h5};
  }
  h6 {
    font-size: ${({ theme }) => theme.fontSizes.h6};
  }

  input,
  textarea,
  button,
  select,
  a,
  [role='button'],
  .rt-resizable-header {
    outline: none;
    body.${IS_TABBING_CLASS} &:focus {
      outline: ${({ theme }) => theme.colors.selectableLightBg} auto 3px;
      outline-offset: 0;
    }
  }

  .display-flex {
    display: flex;
  }

  .flex-fit {
    flex: 1;
  }

  .flex-column {
    flex-direction: column;
  }

  .flex-align-center {
    align-items: center;
  }

  .justify-start {
    justify-content: flex-start;
  }

  .justify-center {
    justify-content: center;
  }

  .justify-around {
    justify-content: space-around;
  }

  .justify-end {
    justify-content: flex-end;
  }

  .justify-evenly {
    justify-content: space-evenly;
  }

  .justify-between {
    justify-content: space-between;
  }

  .flex-wrap {
    flex-wrap: wrap;
  }

  .align-self-stretch {
    align-self: stretch;
  }

  .align-self-center {
    align-self: center;
  }

  .font-bold {
    font-weight: bold;
  }

  .font-italic {
    font-style: italic;
  }

  .display-inline-block {
    display: inline-block;
  }

  .border-box {
    box-sizing: border-box;
  }

  .margin-right-sm {
    margin-right: 5px;
  }

  .margin-left-sm {
    margin-left: 5px;
  }

  .margin-top-sm {
    margin-top: 5px;
  }

  .margin-bottom-sm {
    margin-bottom: 5px;
  }

  .margin-0 {
    margin: 0;
  }

  .margin-right-0 {
    margin-right: 0;
  }

  .margin-left-0 {
    margin-left: 0;
  }

  .margin-top-0 {
    margin-top: 0;
  }

  .margin-bottom-0 {
    margin-bottom: 0;
  }

  .margin-med {
    margin: 10px;
  }

  .margin-right-med {
    margin-right: 10px;
  }

  .margin-left-med {
    margin-left: 10px;
  }

  .margin-top-med {
    margin-top: 10px;
  }

  .margin-bottom-med {
    margin-bottom: 10px;
  }

  .padding-sm {
    padding: 5px;
  }

  .padding-right-sm {
    padding-right: 5px;
  }

  .padding-left-sm {
    padding-left: 5px;
  }

  .padding-top-sm {
    padding-top: 5px;
  }

  .padding-bottom-sm {
    padding-bottom: 5px;
  }

  .padding-0 {
    padding: 0;
  }

  .padding-right-0 {
    padding-right: 0;
  }

  .padding-left-0 {
    padding-left: 0;
  }

  .padding-top-0 {
    padding-top: 0;
  }

  .padding-bottom-0 {
    padding-bottom: 0;
  }

  .padding-med {
    padding: 10px;
  }

  .padding-right-med {
    padding-right: 10px;
  }

  .padding-left-med {
    padding-left: 10px;
  }

  .padding-top-med {
    padding-top: 10px;
  }

  .padding-bottom-med {
    padding-bottom: 10px;
  }

  .font-size-1-em {
    font-size: 1em;
  }

  .fj-title {
    font-weight: 600;
  }

  .fj-button-black-outline {
    background-color: transparent;
    border: 2px solid black;
    color: black;
    border-radius: 10px;
    cursor: pointer;

    &:hover {
      border-color: lighten(black, 50);
      color: lighten(black, 50);
    }
  }

  .fj-button-teal-flat a:hover {
    text-decoration: none;
  }

  .fj-error-container-placeholder {
    min-height: 4em;
    width: 100%;
    display: block;
  }
  .inner-content {
    padding: 6% 0 6% 10%;
  }

  .hide-mobile {
    display: auto;
  }

  .show-mobile {
    display: none;
  }

  .fjg-spacer-row-8 {
    height: 8px;
    float: left;
  }

  .fjg-spacer-row-10 {
    height: 10px;
    float: left;
  }

  @media all and (max-width: 375px) {
    .hide-mobile {
      display: none;
    }
    .show-mobile {
      display: auto;
    }
    .inner-content {
      padding: 6% 0 6% 0%;
    }
  }

  .fjcloud-scrollbar {
    overflow-y: scroll;
  }

  .fjcloud-scrollbar::-webkit-scrollbar {
    width: 10px;
    background-color: ${({ theme }) => theme.colors.defaultLightBackgroundColor};
  }

  .fjcloud-scrollbar::-webkit-scrollbar-thumb {
    background-color: orange;
    border-radius: 4px;
  }

  .fjcloud-first-letter-uppercase {
    text-transform: uppercase;
  }

  .fjcloud-weight-200 {
    font-weight: 200;
  }

  .fjcloud-float-left {
    float: left;
  }

  .fjcloud-float-right {
    float: right;
  }

  .fjcloud-cursor-pointer {
    cursor: pointer;
  }

  .fjcloud-padding-bottom-10 {
    padding-bottom: 10px;
  }

  .fjcloud-text-align-center {
    text-align: center;
  }

  .fjcloud-text-align-left {
    text-align: left;
  }

  .fjcloud-padding-6 {
    padding: 6px;
  }

  .fjcloud-padding-3 {
    padding: 3px;
  }

  .fjcloud-padding-20-10 {
    padding: 20px 10px;
  }

  .fjcloud-padding-vert-10 {
    padding: 10px 0;
  }

  .fjcloud-margin-bottom-10 {
    margin-bottom: 10px;
  }

  .fjcloud-margin-bottom-20 {
    margin-bottom: 20px;
  }

  /*-- our cleafix hack -- */
  .row:before,
  .row:after {
    content: '';
    display: table;
    clear: both;
  }

  [class*='col-'] {
    float: left;
    min-height: 1px;
  }

  .height-30px {
    height: 30px;
  }

  .col-1-3 {
    width: 33.33%;
  }
  .col-2-3 {
    width: 66.67%;
  }
  .col-3-3 {
    width: 100%;
  }

  .row {
    width: 100%;
  }

  /*-- some extra column content styling --*/
  [class*='col-'] > div {
    margin: 0;
  }

  @media all and (max-width: 800px) {
    .row .col-2:last-of-type {
      width: 100%;
    }

    .row .col-31 ~ .col-30 {
      width: 100%;
    }
  }

  ::-webkit-input-placeholder {
    color: ${({ theme }) => theme.colors.defaultFieldFont};
    font-size: 86%;
    letter-spacing: 0.03em;
  }

  :-moz-placeholder {
    /* Firefox 18- */
    color: ${({ theme }) => theme.colors.defaultFieldFont};
  }

  ::-moz-placeholder {
    /* Firefox 19+ */
    color: ${({ theme }) => theme.colors.defaultFieldFont};
  }

  :-ms-input-placeholder {
    color: ${({ theme }) => theme.colors.defaultFieldFont};
  }

  .text-center {
    text-align: center;
  }

  .min-width-0 {
    min-width: 0;
  }

  .min-height-0 {
    min-height: 0;
  }

  .overflow-auto {
    overflow: auto;
  }

  .full-height {
    height: 100%;
  }

  .full-width {
    width: 100%;
  }

  .list-style-none {
    list-style: none;
  }

  i {
    font-style: italic;
  }

  .background-white {
    background-color: ${({ theme }) => theme.colors.rowStripe};
  }

  .fj-button-teal {
    border: 1px solid ${({ theme }) => theme.colors.tealLight};
    border-radius: 0.9em;
    background-color: ${({ theme }) => theme.colors.tealLight};
    color: white;
    padding: 4% 16% 2% 16%;
    text-align: center;
    font-size: 1em;
    text-decoration: none;
    cursor: pointer;
  }

  .fj-button-teal:hover {
    background-color: ${({ theme }) => theme.colors.tealLighter};
    text-decoration: none;
  }

  .fj-button-teal a:hover {
    text-decoration: none;
  }

  .fj-button-tan {
    border: 1px solid ${({ theme }) => theme.colors.tealLight};
    border-radius: 0.9em;
    background-color: ${({ theme }) => theme.colors.lightGreyBackground};
    color: ${({ theme }) => theme.colors.cloudFontGreen};
    padding: 4% 16% 2% 16%;
    text-align: center;
    font-size: 1em;
    text-decoration: none;
    cursor: pointer;
  }

  .fj-button-tan:hover {
    background-color: ${({ theme }) => theme.colors.disabledText};
    text-decoration: none;
  }

  .fj-button-tan a:hover {
    text-decoration: none;
  }

  .fj-button-teal-flat {
    background-color: ${({ theme }) => theme.colors.tealLight};
    color: white;
    padding: 4% 11% 1% 11%;
    text-align: center;
    font-size: 1em;
    text-decoration: none;
    cursor: pointer;
    width: 100%;
  }

  .fj-button-teal-flat:hover {
    background-color: ${({ theme }) => theme.colors.tealLighter};
  }

  .fj-charcoal-font {
    color: ${({ theme }) => theme.colors.cloudCharcoalFont};
  }

  .fj-green-font {
    color: ${({ theme }) => theme.colors.cloudFontGreen};
  }

  .fj-teal-font {
    color: ${({ theme }) => theme.colors.cloudTeal};
  }

  .fj-teal-selectable-font {
    color: ${({ theme }) => theme.colors.selectableLightBg};
  }

  .fj-grey-font {
    color: ${({ theme }) => theme.colors.cloudGreyFont};
  }

  .fj-green-teal-font {
    color: ${({ theme }) => theme.colors.cloudFontGreenTeal};
  }

  .fj-teal-brown {
    color: ${({ theme }) => theme.colors.cloudGreyFont};
  }

  .fj-prevalidation {
    color: ${({ theme }) => theme.colors.alertRed};
    font-size: 80%;
    text-align: left;
  }

  // TODO: (Rane) turn into styled-component
  .fj-error-container {
    border: 1px solid ${({ theme }) => theme.colors.alertRed};
    background-color: lighten(${({ theme }) => theme.colors.alertRed}, 50);
    padding: 3%;
    width: 100%;
    box-sizing: border-box;
  }

  .fj-success-container {
    border: 1px solid ${({ theme }) => theme.colors.successGreen};
    background-color: lighten(${({ theme }) => theme.colors.successGreen}, 50);
    padding: 4.1% 2% 2% 2%;
    width: 100%;
    display: block;
  }

  .fjcloud-color-teal {
    color: ${({ theme }) => theme.colors.cloudTeal};
  }

  .fj-form-label {
    color: ${({ theme }) => theme.colors.cloudCharcoalFont};
    font-weight: 200;
  }

  .fj-tooltip {
    display: flex;
    flex-direction: row;
  }

  .fj-tooltip .icon {
    padding: 0.2rem 0 0 0.2rem;
    justify-content: top;
    cursor: pointer;
  }

  .fj-tooltip-portal .visor {
    display: flex;
    transition: opacity 0.33s ease-in-out;
    position: fixed;
    z-index: 1001;
    width: fit-content;
    overflow: visible !important;
    padding: 0.5rem;
    border-radius: 0.5rem;
    background-color: ${({ theme }) => theme.colors.selected};
    color: ${({ theme }) => theme.colors.text};

    &:after {
      content: '';
      height: 0;
      position: absolute;
      width: 0;
      border: 10px solid transparent;
      left: 20px;
      rotate: 180deg;
      border-bottom-color: ${({ theme }) => theme.colors.selected};
      bottom: -20px;
      z-index: 1;
    }

  }

  a {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.selectableLightBg};
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .warning-red {
    color: ${({ theme }) => theme.colors.accentText};
  }

  .error-red {
    color: ${({ theme }) => theme.colors.redAccentButton};
  }

  .disclaimer-gray {
    color: ${({ theme }) => theme.colors.cloudGreyFont};
  }

  .disclaimer {
    color: ${({ theme }) => theme.colors.cloudGreyFont};
    font-size: 0.9em;
    font-style: italic;
  }

  .border-fj-black {
    border: 1px solid ${({ theme }) => theme.colors.black};
  }

  .fj-header {
    font-size: 20px;
    color: ${({ theme }) => theme.colors.accentText};
  }

  input.FjInput:disabled {
    background-color: ${({ theme }) => theme.colors.defaultLightBackgroundColor};
  }

  .hidden {
    opacity: 0;
    pointer-events: none;
  }

  .visible {
    opacity: 1;
    pointer-events: inherit;
  }

`;

export default withTheme(GlobalStyles);
